/**
 * Meta Pixel controlled wrapper.
 *
 * - Single source of truth for Pixel initialisation and events.
 * - Never loads or fires on /admin/* or Solace Online routes (no prior marketing consent).
 * - Privacy-safe: callers must only pass categorical metadata. No PII.
 */

const PIXEL_ID = "3485636234920047";
const DEDUPE_PREFIX = "mp_dedupe:";
const META_PIXEL_INITIALISED_FLAG = "__solaceMetaPixelInitialised";
const META_PIXEL_LAST_PAGE_VIEW_PATH = "__solaceMetaPixelLastPageViewPath";

type FbqFn = ((...args: unknown[]) => void) & {
  callMethod?: (...args: unknown[]) => void;
  queue?: unknown[];
  push?: unknown;
  loaded?: boolean;
  version?: string;
  _fbq?: unknown;
};

declare global {
  interface Window {
    fbq?: FbqFn;
    _fbq?: FbqFn;
    __solaceMetaPixelInitialised?: boolean;
    __solaceMetaPixelLastPageViewPath?: string;
  }
}

type EventParamValue = string | number | boolean | undefined | null | ReadonlyArray<string | number>;
type EventParams = Record<string, EventParamValue>;

interface EventOptions {
  /** Custom event id sent to Meta as `eventID` (for server/browser dedupe). */
  eventID?: string;
  /** When set, the event will fire at most once per session for this key. */
  dedupeKey?: string;
}

let initialised = false;

/** For now, Online routes do not use advertising pixels absent opt-in consent. */
function isTrackingExcludedPath(): boolean {
  if (typeof window === "undefined") return true;
  const path = window.location.pathname;
  return path.startsWith("/admin") || path.startsWith("/online");
}

export function isMetaPixelAllowed(): boolean {
  return typeof window !== "undefined" && !isTrackingExcludedPath();
}

function currentPageViewPath(): string {
  if (typeof window === "undefined") return "";
  return `${window.location.pathname}${window.location.search}`;
}

function generateEventId(): string {
  try {
    if (typeof crypto !== "undefined" && typeof crypto.randomUUID === "function") {
      return crypto.randomUUID();
    }
  } catch {
    /* ignore */
  }
  // RFC4122-ish fallback
  return "xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx".replace(/[xy]/g, (c) => {
    const r = (Math.random() * 16) | 0;
    const v = c === "x" ? r : (r & 0x3) | 0x8;
    return v.toString(16);
  });
}

function shouldDedupe(dedupeKey?: string): boolean {
  if (!dedupeKey) return false;
  try {
    const key = DEDUPE_PREFIX + dedupeKey;
    if (sessionStorage.getItem(key)) return true;
    sessionStorage.setItem(key, "1");
  } catch {
    /* sessionStorage unavailable — fall through and allow event */
  }
  return false;
}

function loadFbevents(): void {
  // Canonical Meta Pixel base snippet, guarded so it only injects the script once.
  const w = window as Window & { fbq?: FbqFn; _fbq?: FbqFn };
  if (w.fbq) return;
  const n: FbqFn = function (...args: unknown[]) {
    if (n.callMethod) {
      n.callMethod.apply(n, args);
    } else {
      n.queue!.push(args);
    }
  } as FbqFn;
  w.fbq = n;
  if (!w._fbq) w._fbq = n;
  n.push = n;
  n.loaded = true;
  n.version = "2.0";
  n.queue = [];

  const t = document.createElement("script");
  t.async = true;
  t.src = "https://connect.facebook.net/en_US/fbevents.js";
  const s = document.getElementsByTagName("script")[0];
  s?.parentNode?.insertBefore(t, s);
}

function markMetaPixelInitialised(): void {
  window[META_PIXEL_INITIALISED_FLAG] = true;
}

function hasMetaPixelInitBeenSent(): boolean {
  return window[META_PIXEL_INITIALISED_FLAG] === true;
}

function getLastPageViewPath(): string | undefined {
  return window[META_PIXEL_LAST_PAGE_VIEW_PATH];
}

function setLastPageViewPath(path: string): void {
  window[META_PIXEL_LAST_PAGE_VIEW_PATH] = path;
}

function fbqTrack(command: "track" | "trackCustom", eventName: string, params?: EventParams, eventID?: string): void {
  if (typeof window.fbq !== "function") return;
  const safeParams = params ?? {};
  const options = eventID ? { eventID } : undefined;
  window.fbq(command, eventName, safeParams, options);
}

/**
 * Initialise Meta Pixel only once.
 *
 * The inline snippet in index.html performs the first paint init + PageView so
 * Meta can detect the pixel before React hydrates. This helper mirrors that
 * state and only sends init itself when the inline snippet did not run.
 */
export function initMetaPixel(): void {
  if (initialised) return;
  if (!isMetaPixelAllowed()) return;

  if (typeof window.fbq !== "function") {
    loadFbevents();
  }

  if (typeof window.fbq !== "function") return;

  if (!hasMetaPixelInitBeenSent()) {
    window.fbq("init", PIXEL_ID);
    markMetaPixelInitialised();
  }

  initialised = true;
}

/**
 * Fire a standard Meta Pixel event (e.g. "PageView", "Lead", "Contact").
 */
export function track(eventName: string, params?: EventParams, options?: EventOptions): void {
  if (!isMetaPixelAllowed()) return;
  if (!initialised) initMetaPixel();
  if (typeof window.fbq !== "function") return;
  if (shouldDedupe(options?.dedupeKey)) return;

  fbqTrack("track", eventName, params, options?.eventID ?? generateEventId());
}

/**
 * Fire a custom Meta Pixel event.
 */
export function trackCustom(eventName: string, params?: EventParams, options?: EventOptions): void {
  if (!isMetaPixelAllowed()) return;
  if (!initialised) initMetaPixel();
  if (typeof window.fbq !== "function") return;
  if (shouldDedupe(options?.dedupeKey)) return;

  fbqTrack("trackCustom", eventName, params, options?.eventID ?? generateEventId());
}

/**
 * Fire a PageView for SPA route changes. No-op on admin routes and skips the
 * initial React call when index.html already sent the first PageView.
 */
export function trackPageView(): void {
  if (!isMetaPixelAllowed()) return;
  if (!initialised) initMetaPixel();
  if (typeof window.fbq !== "function") return;

  const path = currentPageViewPath();
  if (getLastPageViewPath() === path) return;

  fbqTrack("track", "PageView", undefined, generateEventId());
  setLastPageViewPath(path);
}
